import apiClient from './client';

export interface AdminUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  role: 'SuperAdmin' | 'Admin' | 'Compliance' | 'Operations' | 'Viewer';
  status: 'Active' | 'Inactive' | 'Suspended' | 'PendingInvitation';
  lastLoginAt?: string;
  invitedAt?: string;
  emailConfirmed: boolean;
  twoFactorEnabled: boolean;
}

export interface AdminLoginResponsePayload {
  accessToken?: string;
  refreshToken?: string;
  user?: AdminUser;
  expiresAt?: string;
  requiresTwoFactor?: boolean;
  twoFactorToken?: string;
  requiresTwoFactorSetup?: boolean;
  twoFactorSetupToken?: string;
}

export interface Client {
  id: string;
  publicId?: string;
  email: string;
  firstName?: string;
  lastName?: string;
  /** From Users.company_name (camelCase in JSON). */
  companyName?: string;
  /** Portal profile: Active, Unactive, Restricted, Closed (as returned by API). */
  status: string;
  emailConfirmed: boolean;
  twoFactorEnabled: boolean;
  createdAt?: string;
  lastLoginAt?: string;
  /** BUSINESS | PRIVATE from onboarding (camelCase from API). */
  applicationType?: string;
  /** Client tier ID for fee calculations */
  tierId?: string;
  tier?: ClientTier;
  /** Admin-only: client may receive Digital Incoming payments */
  digitalIncomingEnabled?: boolean;
  /** Admin-only: client may initiate Digital Outgoing payments */
  digitalOutgoingEnabled?: boolean;
}

export interface AdminLoginRequest {
  email: string;
  password: string;
  rememberMe?: boolean;
}


export interface TwoFactorSetupResponse {
  secret: string;
  qrCodeUri: string;
}

export interface InviteAdminRequest {
  email: string;
  role: string;
  message?: string;
}

export interface AdminInvitationPreview {
  email: string;
  role: string;
  expiresAt: string;
}

export interface AcceptInvitationRequest {
  token: string;
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
  phoneNumber?: string;
}

export interface UpdateRoleRequest {
  role: string;
}

export interface UpdateProfileRequest {
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
}

export interface FundAccountRequest {
  amount: number;
  description: string;
}

export interface ClientAccountListItem {
  id: string;
  clientId: string;
  providerId: string;
  currencyId: string;
  availableAccountId: string;
  blockedAccountId: string;
  incomingPendingAccountId: string;
  outgoingPendingAccountId: string;
  restrictedAccountId: string;
  status: string;
  accountName?: string;
  accountNumber?: string;
  bankName?: string;
  swiftCode?: string;
  iban?: string;
  availableBalance: number;
  blockedBalance: number;
  providerName?: string;
}

export interface AccountRequisites {
  id: string;
  clientAccountId: string;
  paymentMethod: 'SWIFT' | 'SEPA';
  accountName?: string | null;
  accountNumber?: string | null;
  iban?: string | null;
  swiftCode?: string | null;
  bankName?: string | null;
  bankLocation?: string | null;
  bankAddress?: string | null;
  branchCode?: string | null;
  bankCode?: string | null;
  intermediaryBankName?: string | null;
  intermediaryBankSwift?: string | null;
  status: 'PENDING' | 'ACTIVE' | 'SUSPENDED' | 'CLOSED';
  isPublished: boolean;
  isPrimary: boolean;
  notes?: string | null;
}

export interface AccountRequisitesSearchHit {
  id: string;
  clientAccountId: string;
  clientId: string;
  paymentMethod: 'SWIFT' | 'SEPA';
  iban?: string | null;
  currencyId: string;
  clientName?: string | null;
}

export interface SaveAccountRequisitesRequest {
  paymentMethod?: 'SWIFT' | 'SEPA';
  accountName?: string;
  accountNumber?: string;
  iban?: string;
  swiftCode?: string;
  bankName?: string;
  bankLocation?: string;
  bankAddress?: string;
  branchCode?: string;
  bankCode?: string;
  intermediaryBankName?: string;
  intermediaryBankSwift?: string;
  status?: 'PENDING' | 'ACTIVE' | 'SUSPENDED' | 'CLOSED';
  isPublished?: boolean;
  isPrimary?: boolean;
  notes?: string;
}

export interface SepaCountry {
  ibanCountryCode: string;
  countryName: string;
  category: string;
  isActive: boolean;
}

export interface ProviderStatementItem {
  id: string;
  providerId: string;
  statementDate: string;
  amount: number;
  currencyId: string;
  providerReference?: string;
  iban?: string;
  description?: string;
  matchStatus: 'MATCHED' | 'UNMATCHED';
  matchedOutgoingPaymentId?: string;
  mismatchReason?: string;
  importedAt: string;
}

export interface SepaRecall {
  id: string;
  outgoingPaymentId: string;
  providerId: string;
  status: 'REQUESTED' | 'SENT_TO_PROVIDER' | 'ACCEPTED' | 'REJECTED' | 'SETTLED';
  reason: string;
  providerReference?: string;
  requestedAt: string;
  resolvedAt?: string;
  resolutionNote?: string;
}

export type UpsertAccountRequisitesRequest = SaveAccountRequisitesRequest;

export interface AdminAccountOpeningRequest {
  id: string;
  userId: string;
  clientPublicId?: string;
  clientFullName?: string;
  clientEmail?: string;
  requestedCurrency: string;
  purpose?: string;
  status: string;
  reviewNotes?: string;
  reviewedAt?: string;
  reviewedByAdminId?: string;
  reviewedByAdminName?: string;
  createdAt: string;
}

export interface ProviderAccountOpeningApplication {
  id: string;
  clientId: string;
  providerId?: string | null;
  processingMode?: 'API' | 'MANUAL' | null;
  status: 'DRAFT' | 'PENDING_INTERNAL' | 'SENT' | 'EXTERNAL_PENDING' | 'APPROVED' | 'RETURNED' | 'DECLINED' | 'CANCELLED';
  purpose?: string | null;
  packageSnapshotJson: string;
  complianceApprovedAt?: string | null;
  reasonCode?: string | null;
  reasonComment?: string | null;
  providerReference?: string | null;
  createdAt: string;
  updatedAt: string;
  items: Array<{
    id: string;
    currencyId: string;
    displayName?: string | null;
    status: 'PENDING' | 'ACTIVE' | 'FAILED' | 'CANCELLED';
    clientAccountId?: string | null;
    providerReference?: string | null;
  }>;
}

export interface SubAccountListItem {
  id: string;
  accountTypeCode: string;
  accountTypeName: string;
  displayName: string;
  currencyId: string;
  status: string;
  clientAccountId: string;
  clientId: string;
  clientPublicId?: string;
  clientFullName: string;
  providerName?: string;
}

export interface OpenClientAccountRequest {
  clientId: string;
  currencyId: string;
}

export interface CreateClientAccountRequest {
  clientId: string;
  providerId: string;
  currencyId: string;
  displayName?: string;
  preferredSendingBank?: string;
  accountName: string;
  requisites?: Array<Required<Pick<UpsertAccountRequisitesRequest, 'paymentMethod'>> & UpsertAccountRequisitesRequest>;
  accountNumber?: string;
  iban?: string;
  bankName: string;
  branchCode?: string;
  bankCode?: string;
  bankLocation: string;
  bankAddress: string;
  swiftCode: string;
  intermediaryBankName?: string;
  intermediaryBankSwift?: string;
}

export interface TopUpClientAccountRequest {
  clientId: string;
  clientAccountId: string;
  amount: number;
  reason: string;
  comment?: string;
}

export interface RebalanceClientAccountsRequest {
  clientId: string;
  fromClientAccountId: string;
  toClientAccountId: string;
  amount: number;
  reason: string;
  comment?: string;
}

export interface CurrencyDto {
  id: string;
  code: string;
  name: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
}

export interface CreateClientRequest {
  companyName: string;
  email: string;
  password: string;
  phoneNumber: string;
  /** Matches client portal registration: `business` → BUSINESS onboarding, `private` → PRIVATE (individual). */
  clientType: 'business' | 'private';
  firstName: string;
  lastName: string;
}

/** GET /admin/dashboard/system-status */
export interface DashboardSystemStatus {
  api: { status: string; checkedAtUtc: string };
  database: { status: string; latencyMs: number | null };
  auditTrail: { status: string; totalEvents: number | null };
}

export const adminApi = {
  // Authentication
  login: (data: AdminLoginRequest) =>
    apiClient.post<AdminLoginResponsePayload>('/admin/auth/login', data),

  verifyTwoFactor: (token: string, code: string) =>
    apiClient.post<AdminLoginResponsePayload>('/admin/auth/login/2fa', {
      twoFactorToken: token,
      code,
    }),

  refreshToken: (refreshToken: string) =>
    apiClient.post<AdminLoginResponsePayload>('/admin/auth/refresh', { refreshToken }),

  logout: () => 
    apiClient.post('/admin/auth/logout'),

  // Profile Management
  getCurrentUser: () => 
    apiClient.get<AdminUser>('/admin/auth/me'),

  updateProfile: (data: UpdateProfileRequest) => 
    apiClient.put<AdminUser>('/admin/auth/me', data),

  changePassword: (data: ChangePasswordRequest) => 
    apiClient.post('/admin/auth/change-password', data),

  // Admin Management
  inviteAdmin: (data: InviteAdminRequest) =>
    apiClient.post('/admin/auth/invite', data),

  getAdmins: () =>
    apiClient.get<AdminUser[]>('/admin/auth/admin-users'),

  getInvitation: (token: string) =>
    apiClient.get<AdminInvitationPreview>('/admin/auth/invitation', { params: { token } }),

  acceptInvitation: (data: AcceptInvitationRequest) =>
    apiClient.post<AdminUser>('/admin/auth/accept-invitation', data),

  updateAdminRole: (adminId: string, data: UpdateRoleRequest) =>
    apiClient.put(`/admin/auth/${adminId}/role`, data),

  suspendAdmin: (adminId: string) =>
    apiClient.post(`/admin/auth/${adminId}/suspend`),

  reactivateAdmin: (adminId: string) =>
    apiClient.post(`/admin/auth/${adminId}/reactivate`),

  // Client Management
  getClients: () => 
    apiClient.get<Client[]>('/admin/auth/clients'),

  createClient: (data: CreateClientRequest) => 
    apiClient.post<Client>('/clients', data),

  getClientById: (id: string) => 
    apiClient.get<Client>(`/admin/users/users/${id}`),

  getAllAccounts: () => 
    apiClient.get<any[]>('/admin/auth/accounts'),

  getClientAccounts: () => 
    apiClient.get<any[]>('/ClientAccount/admin/get-client-accounts'),

  getSubAccounts: () =>
    apiClient.get<SubAccountListItem[]>('/ClientAccount/admin/sub-accounts'),

  getClientAccountById: (accountId: string) => 
    apiClient.get<any>(`/ClientAccount/admin/client/${accountId}`),

  updateAccountStatus: (accountId: string, status: string, comment?: string) =>
    apiClient.put(`/ClientAccount/admin/${accountId}/status`, { status, comment }),

  updateBankingDetails: (accountId: string, data: {
    accountName?: string; accountNumber?: string; iban?: string; bankName?: string;
    branchCode?: string; bankCode?: string; bankLocation?: string;
    bankAddress?: string; swiftCode?: string;
    intermediaryBankName?: string; intermediaryBankSwift?: string;
  }) =>
    apiClient.put(`/ClientAccount/admin/${accountId}/banking-details`, data),

  getAccountRequisites: (accountId: string, activeOnly = false) =>
    apiClient.get<AccountRequisites[]>(`/ClientAccount/${accountId}/requisites`, { params: { activeOnly } }),

  createAccountRequisites: (accountId: string, data: SaveAccountRequisitesRequest) =>
    apiClient.post<{ id: string }>(`/ClientAccount/admin/${accountId}/requisites`, data),

  updateAccountRequisites: (requisitesId: string, data: SaveAccountRequisitesRequest) =>
    apiClient.put(`/ClientAccount/admin/requisites/${requisitesId}`, data),

  updateAccountRequisitesStatus: (requisitesId: string, status: 'PENDING' | 'ACTIVE' | 'SUSPENDED' | 'CLOSED', reason?: string) =>
    apiClient.put(`/ClientAccount/admin/requisites/${requisitesId}/status`, { status, reason }),

  setAccountRequisitesPublication: (requisitesId: string, isPublished: boolean) =>
    apiClient.put(`/ClientAccount/admin/requisites/${requisitesId}/publication`, { isPublished }),

  deactivateAccountRequisites: (requisitesId: string, reason: string) =>
    apiClient.post(`/ClientAccount/admin/requisites/${requisitesId}/deactivate`, { reason }),

  searchRequisitesByIban: (iban: string) =>
    apiClient.get<AccountRequisitesSearchHit[]>('/ClientAccount/admin/requisites/search', { params: { iban } }),

  getSepaCountries: (isActive?: boolean) =>
    apiClient.get<SepaCountry[]>('/admin/sepa-countries', { params: isActive === undefined ? undefined : { isActive } }),

  upsertSepaCountry: (ibanCountryCode: string, data: Omit<SepaCountry, 'ibanCountryCode'>) =>
    apiClient.put(`/admin/sepa-countries/${ibanCountryCode}`, data),

  getProviderStatementItems: (providerId?: string, status?: string) =>
    apiClient.get<ProviderStatementItem[]>('/admin/reconciliation/statement-items', { params: { providerId, status } }),

  importProviderStatement: (providerId: string, file: File) => {
    const data = new FormData();
    data.append('providerId', providerId);
    data.append('file', file);
    return apiClient.post<{ imported: number; duplicates: number; errors: string[] }>('/admin/reconciliation/statement-import', data);
  },

  matchProviderStatementItem: (id: string, outgoingPaymentId: string) =>
    apiClient.put(`/admin/reconciliation/statement-items/${id}/match`, { outgoingPaymentId }),

  getSepaRecalls: () =>
    apiClient.get<SepaRecall[]>('/admin/sepa-recalls'),

  createSepaRecall: (outgoingPaymentId: string, reason: string) =>
    apiClient.post<{ id: string }>('/admin/sepa-recalls', { outgoingPaymentId, reason }),

  updateSepaRecallStatus: (id: string, status: SepaRecall['status'], providerReference?: string, resolutionNote?: string, incomingProviderReturnId?: string) =>
    apiClient.put(`/admin/sepa-recalls/${id}/status`, { status, providerReference, resolutionNote, incomingProviderReturnId }),

  releaseSuspenseFunds: (accountId: string) =>
    apiClient.post<{ message: string; released: number }>(`/ClientAccount/admin/${accountId}/release-suspense`),

  // Account opening requests
  getAccountOpeningRequests: () =>
    apiClient.get<AdminAccountOpeningRequest[]>('/accounts/admin/opening-requests'),

  reviewAccountOpeningRequest: (
    id: string,
    decision: 'APPROVED' | 'REJECTED',
    notes?: string,
    bankingDetails?: {
      providerId?: string;
      accountName?: string;
      accountNumber?: string;
      iban?: string;
      bankName?: string;
      branchCode?: string;
      bankCode?: string;
      bankLocation?: string;
      bankAddress?: string;
      swiftCode?: string;
      intermediaryBankName?: string;
      intermediaryBankSwift?: string;
    },
  ) =>
    apiClient.put(`/accounts/admin/opening-requests/${id}/review`, {
      decision,
      notes,
      ...bankingDetails,
    }),

  getProviderAccountOpeningApplications: () =>
    apiClient.get<ProviderAccountOpeningApplication[]>('/accounts/admin/provider-opening-applications'),

  selectProviderAccountOpeningApplicationProvider: (id: string, providerId: string) =>
    apiClient.post<ProviderAccountOpeningApplication>(`/accounts/admin/provider-opening-applications/${id}/select-provider`, { providerId }),

  submitProviderAccountOpeningApplication: (id: string) =>
    apiClient.post<ProviderAccountOpeningApplication>(`/accounts/admin/provider-opening-applications/${id}/submit-internal`),

  approveProviderAccountOpeningApplication: (id: string) =>
    apiClient.post<ProviderAccountOpeningApplication>(`/accounts/admin/provider-opening-applications/${id}/approve`),

  returnProviderAccountOpeningApplication: (id: string, reasonCode: string, comment?: string) =>
    apiClient.post<ProviderAccountOpeningApplication>(`/accounts/admin/provider-opening-applications/${id}/return`, { reasonCode, comment }),

  declineProviderAccountOpeningApplication: (id: string, reasonCode: string, comment?: string) =>
    apiClient.post<ProviderAccountOpeningApplication>(`/accounts/admin/provider-opening-applications/${id}/decline`, { reasonCode, comment }),

  sendProviderAccountOpeningApplication: (id: string) =>
    apiClient.post<ProviderAccountOpeningApplication>(`/accounts/admin/provider-opening-applications/${id}/send`),

  fundAccount: (accountId: string, data: FundAccountRequest) => 
    apiClient.post(`/admin/auth/accounts/${accountId}/fund`, data),

  // Client accounts by clientId
  getClientAccountsByClientId: (clientId: string) =>
    apiClient.get<ClientAccountListItem[]>(`/ClientAccount/get-by-client-id`, { params: { clientId } }),

  // Open new client account (legacy, used from ClientDetailsModal)
  openClientAccount: (data: OpenClientAccountRequest) =>
    apiClient.post('/ClientAccount', data),

  // Create client account with full banking details (admin only)
  createClientAccount: (data: CreateClientAccountRequest) =>
    apiClient.post('/ClientAccount/admin/create-account', data),

  // Search clients by name / email / publicId (backend-side, debounced)
  searchClients: (q: string) =>
    apiClient.get<any[]>(`/clients/search`, { params: { q } }),

  // Top up client account
  topUpClientAccount: (data: TopUpClientAccountRequest) =>
    apiClient.post('/ClientAccount/admin/client-accounts/top-up', data),

  rebalanceClientAccounts: (data: RebalanceClientAccountsRequest) =>
    apiClient.post('/ClientAccount/admin/client-accounts/rebalance', data),

  // Currencies
  getCurrencies: () =>
    apiClient.get<CurrencyDto[]>('/currency'),

  // Currencies allowed by provider routing for this client and not yet opened
  getOpenableClientCurrencies: (clientId: string) =>
    apiClient.get<CurrencyDto[]>('/ClientAccount/admin/openable-currencies', { params: { clientId } }),

  /** First-time admin login — returns QR URI + secret (no JWT yet). */
  beginMandatoryTwoFactorSetup: (setupToken: string) =>
    apiClient.post<TwoFactorSetupResponse>('/admin/auth/mfa-required/setup', { setupToken }),

  confirmMandatoryTwoFactorSetup: (setupToken: string, code: string) =>
    apiClient.post<AdminLoginResponsePayload>('/admin/auth/mfa-required/confirm', {
      setupToken,
      code,
    }),

  /** Legacy session without MFA — whitelist on API until enabled. */
  setupTwoFactor: () =>
    apiClient.post<TwoFactorSetupResponse>('/admin/auth/2fa/setup'),

  enableTwoFactor: (code: string) =>
    apiClient.post<void>('/admin/auth/2fa/enable', { code }),

  disableTwoFactor: (code: string) =>
    apiClient.post<void>('/admin/auth/2fa/disable', { code }),

  // Dashboard Stats
  getDashboardStats: () => 
    apiClient.get('/admin/dashboard/stats'),

  getDashboardSystemStatus: () =>
    apiClient.get<DashboardSystemStatus>('/admin/dashboard/system-status'),

  // Dynamic Client Tier Management
  getAllTiers: (includeInactive?: boolean) =>
    apiClient.get<ClientTier[]>('/admin/client-tiers', { params: { includeInactive } }),

  getTier: (id: string) =>
    apiClient.get<ClientTier>(`/admin/client-tiers/${id}`),

  createTier: (data: CreateDynamicTierRequest) =>
    apiClient.post<ClientTier>('/admin/client-tiers', data),

  updateTier: (id: string, data: UpdateDynamicTierRequest) =>
    apiClient.put<ClientTier>(`/admin/client-tiers/${id}`, data),

  deleteTier: (id: string) =>
    apiClient.delete(`/admin/client-tiers/${id}`),

  activateTier: (id: string) =>
    apiClient.post(`/admin/client-tiers/${id}/activate`),

  deactivateTier: (id: string) =>
    apiClient.post(`/admin/client-tiers/${id}/deactivate`),

  // Client management within tiers
  getClientsInTier: (tierId: string) =>
    apiClient.get<ClientInTier[]>(`/admin/client-tiers/${tierId}/clients`),

  getUnassignedClients: () =>
    apiClient.get<ClientInTier[]>('/admin/client-tiers/unassigned-clients'),

  assignClientToTier: (tierId: string, clientId: string, reason?: string) =>
    apiClient.post(`/admin/client-tiers/${tierId}/clients/${clientId}`, { reason }),

  removeClientFromTier: (tierId: string, clientId: string) =>
    apiClient.delete(`/admin/client-tiers/${tierId}/clients/${clientId}`),

  bulkAssignClientsToTier: (tierId: string, clientIds: string[], reason?: string) =>
    apiClient.post(`/admin/client-tiers/${tierId}/bulk-assign`, { clientIds, reason }),

  moveClientsBetweenTiers: (fromTierId: string, toTierId: string, clientIds: string[], reason?: string) =>
    apiClient.post('/admin/client-tiers/move-clients', { fromTierId, toTierId, clientIds, reason }),

  // Tier statistics
  getTierStatistics: (tierId: string) =>
    apiClient.get<TierStatistics>(`/admin/client-tiers/${tierId}/statistics`),

  getAllTierStatistics: () =>
    apiClient.get<TierStatistics[]>('/admin/client-tiers/statistics'),

  // Client tier management (single client)
  getClientTier: (clientId: string) =>
    apiClient.get<{ clientId: string; tierId: string | null; tier?: ClientTier }>(`/admin/client-tiers/clients/${clientId}/tier`),

  updateClientTier: (clientId: string, tierId: string | null, reason?: string) =>
    apiClient.put(`/admin/client-tiers/clients/${clientId}/tier`, { tierId, reason }),

  // Tier Fee Rules (updated for dynamic tiers)
  getTierFeeRules: (params?: { providerId?: string; tierId?: string; feeType?: string }) =>
    apiClient.get<ClientTierFeeRule[]>('/admin/client-tier/rules', { params }),

  createTierFeeRule: (data: CreateTierFeeRuleRequest) =>
    apiClient.post<ClientTierFeeRule>('/admin/client-tier/rules', data),

  updateTierFeeRule: (id: string, data: UpdateTierFeeRuleRequest) =>
    apiClient.put<ClientTierFeeRule>(`/admin/client-tier/rules/${id}`, data),

  deactivateTierFeeRule: (id: string) =>
    apiClient.post(`/admin/client-tier/rules/${id}/deactivate`),

  deleteTierFeeRule: (id: string) =>
    apiClient.delete(`/admin/client-tier/rules/${id}`),

  // Tier Fee Calculation
  calculateTierFee: (data: CalculateTierFeeRequest) =>
    apiClient.post<CalculateTierFeeResult>('/admin/client-tier/calculate', data),

  getAvailableFeeTypes: () =>
    apiClient.get<Array<{ value: string; name: string; description: string }>>('/admin/client-tier/fee-types'),

  getPaymentFeeCalculation: (paymentType: string, paymentId: string) =>
    apiClient.get<FeeCalculationSnapshot>(`/admin/payment-fee-calculations/${paymentType}/${paymentId}`),
};

// Dynamic Client Tier Types
export interface FeeCalculationSnapshot {
  feeRuleId?: string;
  calculationType: string;
  calculationPercentage: number;
  calculationFixedAmount: number;
  percentagePartAmount: number;
  fixedPartAmount: number;
  minApplied: boolean;
  maxApplied: boolean;
  baseAmount: number;
  calculatedAmount: number;
  finalAmount: number;
  currencyId: string;
  isOverridden: boolean;
}

export interface ClientTier {
  id: string;
  code: string;
  name: string;
  description?: string;
  color?: string;
  icon?: string;
  priority: number;
  isActive: boolean;
  isSystem: boolean;
  maxClients?: number;
  requirements?: string;
  clientCount?: number;
  feeRuleCount?: number;
  createdAt: string;
}

export interface CreateDynamicTierRequest {
  code: string;
  name: string;
  description?: string;
  color?: string;
  icon?: string;
  priority?: number;
  maxClients?: number;
  requirements?: string;
}

export interface UpdateDynamicTierRequest {
  name: string;
  description?: string;
  color?: string;
  icon?: string;
  priority: number;
  isActive: boolean;
  maxClients?: number;
  requirements?: string;
}

export interface ClientInTier {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  publicId?: string;
  status: string;
  createdAt?: string;
}

export interface TierStatistics {
  tierId: string;
  tierName: string;
  tierCode: string;
  totalClients: number;
  activeClients: number;
  inactiveClients: number;
  feeRulesCount: number;
  totalVolumeLast30Days?: number;
  totalFeesLast30Days?: number;
}

// Tier Fee Types (updated for dynamic tiers)
export interface ClientTierFeeRule {
  id: string;
  providerId?: string;
  providerName?: string;
  tierId: string;
  tierName?: string;
  tierCode?: string;
  feeType: 'OUTGOING_PAYMENT' | 'INCOMING_PAYMENT' | 'DIGITAL_OUTGOING' | 'DIGITAL_INCOMING' | 'INTERNAL_TRANSFER' | 'FX_MARKUP';
  calculationMode: 'FIXED' | 'PERCENTAGE' | 'PERCENTAGE_PLUS_FIXED';
  fixedAmount: number;
  percentOfAmount: number;
  calculationPercentage: number;
  calculationFixedAmount: number;
  minFee: number;
  maxFee: number;
  currencyId?: string;
  feeCurrencyMode?: 'SAME_AS_PAYMENT' | 'FIXED_CURRENCY';
  minAmount?: number;
  maxAmount?: number;
  paymentCurrencyId?: string;
  paymentMethod?: 'SWIFT' | 'SEPA';
  priority: number;
  isActive: boolean;
  effectiveFrom: string;
  effectiveTo?: string;
  description?: string;
  createdAt: string;
}

export interface CreateTierFeeRuleRequest {
  providerId?: string;
  tierId: string;
  feeType: 'OUTGOING_PAYMENT' | 'INCOMING_PAYMENT' | 'DIGITAL_OUTGOING' | 'DIGITAL_INCOMING' | 'INTERNAL_TRANSFER' | 'FX_MARKUP';
  calculationMode: 'FIXED' | 'PERCENTAGE' | 'PERCENTAGE_PLUS_FIXED';
  fixedAmount: number;
  percentOfAmount: number;
  calculationPercentage: number;
  calculationFixedAmount: number;
  minFee: number;
  maxFee: number;
  currencyId?: string;
  feeCurrencyMode?: 'SAME_AS_PAYMENT' | 'FIXED_CURRENCY';
  minAmount?: number;
  maxAmount?: number;
  paymentCurrencyId?: string;
  paymentMethod?: 'SWIFT' | 'SEPA';
  priority?: number;
  effectiveFrom: string;
  effectiveTo?: string;
  description?: string;
  reason: string;
}

export interface UpdateTierFeeRuleRequest {
  id: string;
  providerId?: string;
  tierId: string;
  feeType: 'OUTGOING_PAYMENT' | 'INCOMING_PAYMENT' | 'DIGITAL_OUTGOING' | 'DIGITAL_INCOMING' | 'INTERNAL_TRANSFER' | 'FX_MARKUP';
  calculationMode: 'FIXED' | 'PERCENTAGE' | 'PERCENTAGE_PLUS_FIXED';
  fixedAmount: number;
  percentOfAmount: number;
  calculationPercentage: number;
  calculationFixedAmount: number;
  minFee: number;
  maxFee: number;
  currencyId?: string;
  feeCurrencyMode?: 'SAME_AS_PAYMENT' | 'FIXED_CURRENCY';
  minAmount?: number;
  maxAmount?: number;
  paymentCurrencyId?: string;
  paymentMethod?: 'SWIFT' | 'SEPA';
  priority?: number;
  isActive: boolean;
  effectiveFrom: string;
  effectiveTo?: string;
  description?: string;
  reason: string;
}

export interface CalculateTierFeeRequest {
  clientId: string;
  providerId?: string;
  feeType: 'OUTGOING_PAYMENT' | 'INCOMING_PAYMENT' | 'DIGITAL_OUTGOING' | 'DIGITAL_INCOMING' | 'INTERNAL_TRANSFER' | 'FX_MARKUP';
  amount: number;
  currencyId?: string;
}

export interface CalculateTierFeeResult {
  feeAmount: number;
  originalAmount: number;
  totalAmount: number;
  tierId?: string;
  tierName?: string;
  tierCode?: string;
  appliedRuleId?: string;
  calculationDetails: string;
}
